import { Link } from "@tanstack/react-router";
import { Download, Trash2 } from "lucide-react";
import { useState } from "react";

import { AssetLabel } from "@/components/asset-label";
import { CollapsibleCard } from "@/components/collapsible-card";
import { type Column, DataList } from "@/components/data-list";
import { QueryState } from "@/components/query-state";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import type { DayStats, OpportunityStats, PairStats, StatsBucket } from "@/generated/api/model";
import { useGetOpportunityStats } from "@/generated/api/opportunities/opportunities";
import { tokenIdOfPair } from "@/lib/cardanoscan";
import { cn } from "@/lib/utils/cn";

import { ada, downloadCsv, duration, retentionFooter, toCsv } from "./format";
import { NotificationsSummary } from "./NotificationsCard";
import { PruneModal } from "./PruneModal";
import { RANGES } from "./search";

/** Range totals and the per-day table, for every pair or, given `pair`, for that one only. */
export function StatsSummary({ pair, days, onDays }: { pair?: string; days: number; onDays: (days: number) => void }) {
    const [pruneOpen, setPruneOpen] = useState(false);
    const stats = useGetOpportunityStats({ days, pair });

    return (
        <div className="space-y-3 md:space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex gap-1.5" role="group" aria-label="Range">
                    {RANGES.map((range) => (
                        <Button
                            key={range}
                            size="sm"
                            variant={range === days ? "default" : "outline"}
                            aria-pressed={range === days}
                            onClick={() => onDays(range)}
                        >
                            {range} days
                        </Button>
                    ))}
                </div>
                <div className="flex items-center gap-1.5">
                    <Button size="sm" variant="outline" onClick={() => setPruneOpen(true)}>
                        <Trash2 />
                        Prune
                    </Button>
                    {stats.data && (
                        <Button size="sm" variant="outline" onClick={() => downloadDays(stats.data, pair)}>
                            <Download />
                            CSV
                        </Button>
                    )}
                </div>
            </div>
            {pair === undefined && <NotificationsSummary />}
            <QueryState query={stats} isEmpty={() => false} emptyMessage="">
                {(data) => <Overview stats={data} showPairs={pair === undefined} />}
            </QueryState>
            <PruneModal open={pruneOpen} onClose={() => setPruneOpen(false)} />
        </div>
    );
}

function Overview({ stats, showPairs }: { stats: OpportunityStats; showPairs: boolean }) {
    const { totals } = stats;
    return (
        <>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-4" aria-label="Totals">
                <Tile label="Actionable" value={`${totals.actionable} of ${totals.opportunities}`} />
                <Tile label="Potential" value={`${ada(totals.potential_profit_lovelace)} ADA`} accent />
                <Tile label="Best" value={`${ada(totals.best_profit_lovelace)} ADA`} />
                <Tile label="Open now" value={String(stats.open_now)} />
            </div>
            <CollapsibleCard
                title="Per day"
                hint="UTC, by opening day"
                storageKey="stats.per-day"
                contentClassName="px-2 sm:px-4"
            >
                <DataList rows={stats.days} columns={DAY_COLUMNS} rowKey={(day) => day.date} label="Per day" />
            </CollapsibleCard>
            {showPairs && (
                <CollapsibleCard
                    title="Top pairs"
                    hint="tap one for its breakdown"
                    storageKey="stats.top-pairs"
                    contentClassName="px-2 sm:px-4"
                >
                    {stats.top_pairs.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No opportunity in this range.</p>
                    ) : (
                        <DataList
                            rows={stats.top_pairs}
                            columns={PAIR_COLUMNS}
                            rowKey={(pair) => pair.pair}
                            label="Top pairs"
                        />
                    )}
                </CollapsibleCard>
            )}
            <footer className="pt-2 text-center text-xs text-muted-foreground">{retentionFooter(stats)}</footer>
        </>
    );
}

function Tile({ label, value, accent = false }: { label: string; value: string; accent?: boolean }) {
    return (
        <Card className="p-3">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
            <p className={cn("text-lg font-semibold tabular-nums", accent && "text-success")}>{value}</p>
        </Card>
    );
}

const bucketColumns = <T extends StatsBucket>(): Column<T>[] => [
    { label: "Actionable", value: (row) => `${row.actionable} / ${row.opportunities}`, className: "tabular-nums" },
    { label: "Potential ADA", value: (row) => ada(row.potential_profit_lovelace), className: "tabular-nums" },
    { label: "Best ADA", value: (row) => ada(row.best_profit_lovelace), className: "tabular-nums" },
    { label: "Avg open", value: (row) => duration(row.avg_duration_secs) },
    { label: "Longest", value: (row) => duration(row.longest_duration_secs) },
];

const DAY_COLUMNS: Column<DayStats>[] = [{ label: "Day", value: (day) => day.date, title: true }, ...bucketColumns()];

const PAIR_COLUMNS: Column<PairStats>[] = [
    {
        label: "Token",
        title: true,
        render: (pair) => (
            <AssetLabel id={tokenIdOfPair(pair.pair)} name={pair.token_name}>
                <Link
                    to="/stats/pairs/$pair"
                    params={{ pair: pair.pair }}
                    className="font-semibold underline underline-offset-4"
                >
                    {pair.token_name}
                </Link>
            </AssetLabel>
        ),
    },
    ...bucketColumns(),
];

function downloadDays(stats: OpportunityStats, pair: string | undefined) {
    const csv = toCsv(
        ["date", "opportunities", "actionable", "potential_ada", "best_ada", "avg_open_secs", "longest_open_secs"],
        stats.days.map((day) => [
            day.date,
            day.opportunities,
            day.actionable,
            ada(day.potential_profit_lovelace),
            ada(day.best_profit_lovelace),
            day.avg_duration_secs,
            day.longest_duration_secs,
        ]),
    );
    const scope = pair ? `${stats.top_pairs[0]?.token_name ?? "pair"}-` : "";
    downloadCsv(`arbitrage-opportunities-${scope}per-day-${stats.days.at(-1)?.date}-${stats.days[0]?.date}.csv`, csv);
}
